import React, { useEffect, useState } from "react";
import { get, set } from "lodash";
import Row from "@amzn/meridian/row";
import ButtonGroup, { ButtonOption } from "@amzn/meridian/button-group";
import Column from "@amzn/meridian/column";
import Heading from "@amzn/meridian/heading";
import Tab, { TabGroup } from "@amzn/meridian/tab";

import { ApproachComponent, IAppealProps } from "../approach-table/ApproachComponent";
import { MarketCapSpaceView } from "./MarketCapSpaceView";
import { SpaceGrid } from "../commons/SpaceGrid";
import { SpaceToggleComponent } from "../commons/SpaceToggleComponent";
import {
    incomeExpenseProFormaTableEightMapping,
    incomeExpenseProFormaTableFiveMapping,
    incomeExpenseProFormaTableFourMapping,
    incomeExpenseProFormaTableOneMapping,
    incomeExpenseProFormaTableSevenMapping,
    incomeExpenseProFormaTableSixMapping,
    incomeExpenseProFormaTableThreeMapping,
    incomeExpenseProFormaTableTwoMapping,
} from "../commons/spaceTableDefinition";

type IncomeApproachType = "leaseAdjustment" | "marketCap" | "incomeAndExpenseProForma";

export const IncomeApproachComponent: React.FC<IAppealProps> = ({
    data,
    handleUpdateData,
    isEditing,
    refetch,
}) => {
    const [currentTab, setCurrentTab] = useState<string>("");
    const [incomeBtnGroup, setIncomeBtnGroup] = useState<IncomeApproachType>("leaseAdjustment");

    const spaces = get(data, "appealDetails.appealApproachCalculationData.income.spaces", {});

    useEffect(() => {
        if (currentTab === "" && Object.keys(spaces).length > 0) {
            const firstSpaceId = Object.keys(spaces)[0];
            const firstSpace = spaces[firstSpaceId];
            const spaceType = get(firstSpace, "spaceDetails.spaceType", "");
            setCurrentTab(`${spaceType}-${firstSpaceId}`);
        }
    }, [data, currentTab]);

    const handleInputChange = (field: string, value: any) => {
        set(data, field, value);
        handleUpdateData(data);
    };

    const renderIncomeAndExpenseProForma = (spaceId: string) => (
        <>
            <SpaceToggleComponent
                data={data}
                isEditing={isEditing}
                spaceId={spaceId}
                handleInputChange={handleInputChange}
            />
            {[
                { title: "Operating income", mapping: incomeExpenseProFormaTableTwoMapping },
                { title: "", mapping: incomeExpenseProFormaTableThreeMapping },
                {
                    title: "Fixed expenses",
                    mapping: incomeExpenseProFormaTableFourMapping,
                    customHeader: "% of EGI",
                },
                {
                    title: "Variable expenses",
                    mapping: incomeExpenseProFormaTableFiveMapping,
                    customHeader: "% of EGI",
                },
                { title: "", mapping: incomeExpenseProFormaTableSixMapping },
                {
                    title: "Capital expenditures",
                    mapping: incomeExpenseProFormaTableSevenMapping,
                    customHeader: "% of EGI",
                },
                { title: "", mapping: incomeExpenseProFormaTableEightMapping },
            ].map(({ title, mapping, customHeader }, index) => (
                <SpaceGrid
                    key={index}
                    title={title}
                    mapping={mapping(data, spaceId)}
                    isEditing={isEditing}
                    handleInputChange={handleInputChange}
                    data={data}
                    customHeader={customHeader}
                />
            ))}
        </>
    );

    const renderApproachContent = (spaceId: string, space: any) => {
        const spaceType = get(space, "spaceDetails.spaceType", "");
        const tabId = `${spaceType}-${spaceId}`; // Create unique identifier
        if (currentTab !== tabId) return null;

        switch (incomeBtnGroup) {
            case "leaseAdjustment":
                return (
                    <ApproachComponent
                        data={data}
                        handleUpdateData={handleUpdateData}
                        isEditing={isEditing}
                        viewType="INCOME"
                        approachType={incomeBtnGroup}
                        spaceId={spaceId}
                        refetch={refetch}
                    />
                );
            case "marketCap":
                return (
                    <MarketCapSpaceView
                        data={data}
                        isEditing={isEditing}
                        spaceId={spaceId}
                        handleInputChange={handleInputChange}
                        handleUpdateData={handleUpdateData}
                    />
                );
            case "incomeAndExpenseProForma":
                return renderIncomeAndExpenseProForma(spaceId);
            default:
                return null;
        }
    };

    return (
        <>
            <Row width="100%" widths="fill" spacingInset="400 0 400 0">
                <Column alignmentHorizontal="start">
                    <Heading level={3}>Income Approach</Heading>
                </Column>
                <Column alignmentHorizontal="end">
                    <ButtonGroup value={incomeBtnGroup} onChange={setIncomeBtnGroup} size={"small"}>
                        <ButtonOption value="leaseAdjustment">Lease Adjustment</ButtonOption>
                        <ButtonOption value="marketCap">
                            Market Cap, vacancy and rental rate
                        </ButtonOption>
                        <ButtonOption value="incomeAndExpenseProForma">
                            Income & Expense Pro Forma
                        </ButtonOption>
                    </ButtonGroup>
                </Column>
            </Row>
            {incomeBtnGroup === "incomeAndExpenseProForma" && (
                <SpaceGrid
                    title="Income & Expense Pro Forma"
                    mapping={incomeExpenseProFormaTableOneMapping(data)}
                    isEditing={isEditing}
                    handleInputChange={handleInputChange}
                    data={data}
                    backgroundColor="subdued"
                    spacingInset="500"
                    spacing="500"
                />
            )}
            <Column>
                <TabGroup value={currentTab} onChange={setCurrentTab} aria-labelledby="spaces-tabs">
                    {Object.entries(spaces).map(([spaceId, space]) => {
                        const spaceType = get(space, "spaceDetails.spaceType", "");
                        const tabId = `${spaceType}-${spaceId}`; // Create unique identifier

                        return (
                            <Tab key={spaceId} value={tabId} id={spaceId}>
                                {get(space, "spaceDetails.spaceName", "") || spaceType}
                            </Tab>
                        );
                    })}
                </TabGroup>
                {Object.entries(spaces).map(([spaceId, space]) =>
                    renderApproachContent(spaceId, space)
                )}
            </Column>
        </>
    );
};
